import React, { useEffect, useState } from 'react';
import {
  Col,
  Form,
  Input,
  Row,
  Typography,
  Button,
  Tooltip,
  Spin,
} from 'antd';
import PropTypes from 'prop-types';
import toCurrency from 'utils/toCurrency';
import styled from 'styled-components';
import { toFixed, toMoment } from 'utils/normalizers';
import { getTasasDePredios } from 'api/catastro';
import { SyncOutlined } from '@ant-design/icons';
import { patchPredio, dateKeysPredio } from 'api/catastro/predio';
import formatValues from 'utils/formatValues';

const inputStyle = { borderColor: 'unset !important' };
const anioMoment = toMoment(new Date());
const anio_tasa = parseInt(anioMoment.format('YYYY'), 10);

const Container = styled(Col)`
  .ant-form-item-label {
    display: block;
    text-align: center;
  }
  #valor_catastral {
    background-color: ${({ theme }) => theme.activeBackgroundColor};
  }
  .gray-bg .ant-input {
    background-color: #848484;
  }

  input {
    border-color: unset !important;
    text-align: center !important;
    ::placeholder {
      color: #000 !important;
    }
  }
`;

const ValoresDePredio = ({
  predio: _predio,
  construcciones,
  onUpdatePredio,
  mutable,
  noAfectarPadron,
  changePredioPermission,
}) => {
  const predio = { ..._predio };
  const tasaPredio = predio?.tasa ? toFixed(predio?.tasa, 6) : null;
  const tramite = /registrar-tramite/.test(window.location.href);
  const form = tramite ? Form.useFormInstance() : Form.useForm()[0];
  const [tasaAplicable, setTasa] = useState();
  const [loading, setLoading] = useState(true);
  const tipo_de_predio = predio.tipo_de_predio?.id || predio.tipo_de_predio;
  const urbano = tipo_de_predio === 1;
  const construido = predio.tipo_de_predio_urbano === 1;
  const hasAllNeededValues = !!tipo_de_predio && !!predio.tipo_de_predio_urbano;

  useEffect(() => {
    const fetch = async () => {
      if (!hasAllNeededValues) {
        setLoading(false);
        return;
      }
      const tasas = (await getTasasDePredios({
        tipo_de_predio,
        tipo_urbano: predio.tipo_de_predio_urbano,
        anio_tasa,
      }));
      const _tasa = tasas.find((t) => predio.valor_catastral <= t.limite_superior
        && predio.valor_catastral >= t.limite_inferior);
      if (_tasa?.cuota_fija) {
        _tasa.cuota_fija = toFixed(_tasa.cuota_fija, 6);
      }
      setTasa(_tasa);
      setLoading(false);
    };
    if (predio) setTimeout(fetch);
  }, [
    tasaPredio,
    _predio.tipo_de_predio,
    _predio.tipo_de_predio_urbano,
    _predio.superficie_de_la_construccion,
    _predio.superficie_total_del_terreno,
    _predio.valor_del_terreno,
    _predio.valor_de_la_construccion,
    _predio.valor_catastral,
  ]);

  const onClickUpdatePredio = async () => {
    setLoading(true);
    let values = { tasa: tasaAplicable.cuota_fija };
    if (noAfectarPadron) {
      let json_predio = { ...predio };
      Object.keys(values).forEach((key) => {
        json_predio[key] = values[key];
      });
      json_predio = JSON.stringify(formatValues(json_predio, { clean: false }, dateKeysPredio));
      values = { json_predio };
    }
    const saved = await patchPredio(predio.id, values);
    if (saved) {
      onUpdatePredio(noAfectarPadron ? { json_predio: saved.json_predio } : { tasa: saved.tasa });
    }
    setLoading(false);
  };

  const getAddonAfterTasa = () => {
    if (!hasAllNeededValues || !tasaAplicable?.cuota_fija) return null;
    if (tasaAplicable.cuota_fija !== tasaPredio) {
      return (
        <Tooltip
          title={changePredioPermission ? (
            <>
              Tasa guardada en predio
              {` (${tasaPredio}) `}
              no concuerda con tasa encontrada
              {` (${tasaAplicable.cuota_fija}%) `}
              presione para corregir
            </>
          ) : 'Hacen falta permisos para realizar esta acción'}
        >
          <Button
            type="link"
            disabled={!tasaAplicable || !mutable || !changePredioPermission}
            onClick={onClickUpdatePredio}
            style={{ padding: 0, fontSize: 12 }}
          >
            <SyncOutlined />
          </Button>
        </Tooltip>
      );
    }

    return null;
  };

  const getTooltipTasa = () => {
    if (!hasAllNeededValues) return null;
    if (!tasaAplicable) return 'No se encontró tasa correspondiente';
    if (tasaAplicable.cuota_fija !== tasaPredio) return 'Tasa guardada en predio es incorrecta';
    return null;
  };

  const ContentWrapper = tramite ? Col : Form;
  const contentWrapperProps = tramite ? { span: 24 } : { form, layout: 'vertical', style: { marginTop: 35 } };

  return (
    <Spin spinning={loading}>
      <Container>
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <ContentWrapper {...contentWrapperProps}>
          <br />
          <Typography.Title level={4}>
            Valores
          </Typography.Title>
          <br />
          <Row gutter={10}>
            <Col xs={24} sm={24} md={8} lg={6}>
              <Form.Item
                name={construcciones ? 'superficie_de_la_construccion' : 'superficie_total_del_terreno'}
                label={`Superficie ${construcciones ? 'construcción' : 'terreno'}`}
                hasFeedback={false}
              >
                <Input
                  placeholder={construcciones
                    ? toCurrency(predio.superficie_de_la_construccion, false, 3)
                    : toCurrency(predio.superficie_total_del_terreno, false, 3)}
                  style={inputStyle}
                  disabled
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8} lg={6}>
              <Form.Item
                name="valor_del_terreno"
                label="Valor terreno"
                hasFeedback={false}
              >
                <Input
                  disabled
                  style={inputStyle}
                  placeholder={toCurrency(predio.valor_del_terreno, false, 3)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8} lg={6}>
              <Form.Item
                name="valor_de_la_construccion"
                label="Valor construcción"
                hasFeedback={false}
              >
                <Input
                  disabled
                  style={inputStyle}
                  placeholder={toCurrency(predio.valor_de_la_construccion, false, 3)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8} lg={6}>
              <Form.Item
                name="valor_catastral"
                label="Valor fiscal"
                hasFeedback={false}
              >
                <Input
                  disabled
                  style={inputStyle}
                  className="primary-bg"
                  placeholder={toCurrency(predio.valor_catastral, false, 3)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8} lg={6}>
              <Form.Item
                className="gray-bg"
                name="tipo_de_predio"
                label="Tipo de predio"
                hasFeedback={false}
              >
                <Input
                  disabled
                  style={inputStyle}
                  placeholder={predio.tipo_de_predio && (urbano ? 'URBANO' : 'RURAL')}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8} lg={6}>
              <Form.Item
                className="gray-bg"
                name="tipo_de_predio_urbano"
                label="Tipo de predio urbano"
                hasFeedback={false}
              >
                <Input
                  disabled
                  style={inputStyle}
                  placeholder={predio.tipo_de_predio_urbano && (construido ? 'CONSTRUIDO' : 'BALDIO')}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8} lg={6}>
              <Form.Item
                className="gray-bg"
                name="tasa"
                label="Tasa %"
                hasFeedback={false}
                tooltip={getTooltipTasa()}
              >
                <Input
                  disabled
                  style={inputStyle}
                  addonAfter={getAddonAfterTasa()}
                  placeholder={tasaPredio?.toString().concat('%')}
                />
              </Form.Item>
            </Col>
          </Row>
        </ContentWrapper>
      </Container>
    </Spin>
  );
};

ValoresDePredio.propTypes = {
  construcciones: PropTypes.bool,
  mutable: PropTypes.bool,
  onUpdatePredio: PropTypes.func.isRequired,
  predio: PropTypes.shape().isRequired,
  jsonPredio: PropTypes.shape(),
  noAfectarPadron: PropTypes.bool,
  changePredioPermission: PropTypes.bool,
};

ValoresDePredio.defaultProps = {
  construcciones: false,
  mutable: true,
  noAfectarPadron: false,
  jsonPredio: null,
  changePredioPermission: false,
};

export default ValoresDePredio;
